import { type UnitPerformanceDataResponseEntry } from '../../../api/customEndpoints/fetchUnitPerformanceScores';
import { type UseQueryResult } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Error } from '../../../components/admin/error';
import { Loading } from '../../../components/admin/loading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Download } from 'lucide-react';
import { saveSpreadsheet } from '../../../utils/spreadsheet/save';
import { KpiPerformanceBadge } from '../../Kpis/KpiData/KpiPerformanceBadge';
import { useTranslate } from 'ra-core';
import { formatDate, formatNumber } from './formatters';

export function UnitPerformanceKpiData({
  kpiData,
  unitName,
}: {
  kpiData: UseQueryResult<UnitPerformanceDataResponseEntry[]>;
  unitName: string;
}) {
  const translate = useTranslate();

  async function handleExport() {
    if (!kpiData.data) return;

    const mapped = kpiData.data.map((kpi) => {
      return {
        'KPI ID': kpi.id,
        'KPI name': kpi.name,
        // Should (roughly) match the fields in the below table
        Target: kpi.latestTarget,
        Actual: kpi.latestActual,
        Performance: kpi.latestPerformance,
        'vs Last Entry': kpi.vsLastEntry,
        Date: kpi.latestCreatedOn,
        'Prev Target': kpi.previousTarget,
        'Prev Actual': kpi.previousActual,
        'Prev Performance': kpi.previousPerformance,
        'Prev Date': kpi.previousCreatedOn,
      };
    });

    await saveSpreadsheet({
      format: 'csv',
      data: mapped,
      basename: `${unitName} - KPI data`,
    });
  }

  if (kpiData.isPending) {
    return <Loading />;
  } else if (kpiData.isError) {
    return (
      <Error
        resetErrorBoundary={() => {
          void kpiData.refetch();
        }}
        error={kpiData.error}
      />
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">
          {translate('moonstar.unitPerformance.kpiData.title')}
        </h2>
        <Button variant="outline" onClick={() => void handleExport()}>
          <Download />
          {translate('ra.action.export')}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {translate('moonstar.unitPerformance.kpiData.columns.kpi')}
            </TableHead>
            <TableHead>
              {translate('moonstar.unitPerformance.kpiData.columns.target')}
            </TableHead>
            <TableHead>
              {translate('moonstar.unitPerformance.kpiData.columns.actual')}
            </TableHead>
            <TableHead>
              {translate(
                'moonstar.unitPerformance.kpiData.columns.performance'
              )}
            </TableHead>
            <TableHead>
              {translate(
                'moonstar.unitPerformance.kpiData.columns.vsLastEntry'
              )}
            </TableHead>
            <TableHead>
              {translate('moonstar.unitPerformance.kpiData.columns.date')}
            </TableHead>
            <TableHead>
              {translate(
                'moonstar.unitPerformance.kpiData.columns.previousTarget'
              )}
            </TableHead>
            <TableHead>
              {translate(
                'moonstar.unitPerformance.kpiData.columns.previousActual'
              )}
            </TableHead>
            <TableHead>
              {translate(
                'moonstar.unitPerformance.kpiData.columns.previousPerformance'
              )}
            </TableHead>
            <TableHead>
              {translate(
                'moonstar.unitPerformance.kpiData.columns.previousDate'
              )}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {kpiData.data.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell className="font-bold">{entry.name}</TableCell>
              <TableCell>{formatNumber(entry.latestTarget)}</TableCell>
              <TableCell>{formatNumber(entry.latestActual)}</TableCell>
              <TableCell>
                {entry.latestPerformance === undefined ? undefined : (
                  <KpiPerformanceBadge
                    value={parseFloat(entry.latestPerformance)}
                  />
                )}
              </TableCell>
              <TableCell>
                {entry.vsLastEntry === undefined
                  ? ''
                  : new Intl.NumberFormat(undefined, {
                      signDisplay: 'always',
                      style: 'percent',
                    }).format(parseFloat(entry.vsLastEntry))}
              </TableCell>
              <TableCell>{formatDate(entry.latestCreatedOn)}</TableCell>
              <TableCell>{formatNumber(entry.previousTarget)}</TableCell>
              <TableCell>{formatNumber(entry.previousActual)}</TableCell>
              <TableCell>
                {entry.previousPerformance === undefined ? undefined : (
                  <KpiPerformanceBadge
                    value={parseFloat(entry.previousPerformance)}
                  />
                )}
              </TableCell>
              <TableCell>{formatDate(entry.previousCreatedOn)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
